import { CustomButton } from '@ui-kit'
import { LoginTypeEnum } from '@app-store'
import { NAMESPACES } from '../../utils/constants'
import { persist, store, useAppDispatch } from '../../utils/agent.store'
import { performAgentDeskLogout } from '../../session/agentLogout'
import { invalidateToken } from '../../utils/helperFunctions'

const UserNotFoundPage: React.FC = () => {
  const dispatch = useAppDispatch()

  /**
   * Perform a REAL logout, not just a navigation.
   *
   * This button used to do `window.location.href = origin`, which left the
   * backend session cookie intact. The boot bootstrap then silently
   * re-authenticated from that cookie (no Microsoft prompt needed), the profile
   * check failed again for the same reason, and the user was sent straight back
   * here — so the only button on the page restarted the very loop it was meant
   * to escape, with no way out short of clearing cookies by hand.
   *
   * Revoking the session server-side breaks the loop: without the cookie the
   * next sign-in requires a genuine Microsoft round trip.
   *
   * `preserveTableColumn` is not called here any more — performAgentDeskLogout
   * already does it, so doing it twice was redundant.
   */
  const handleLogoutClick = () => {
    void performAgentDeskLogout({
      dispatch,
      persist,
      // clearSession() ran before this page was reached, so there is normally no
      // token left. That is fine: the MS_BACKEND logout is proven by the session
      // cookie + CSRF rather than a bearer, so the revoke still succeeds. Only
      // the audit-log entry is skipped, which needs a token.
      token: store.getState().session.token,
      loginType: LoginTypeEnum.USER_INITIATED_LOGOUT,
      invalidateToken,
    })
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-accent-100">
      <div className="bg-accent-0 rounded-lg shadow-md p-6 max-w-md text-center">
        <h1 className="text-base font-medium text-error-500">Access Denied</h1>
        <p className="mt-4 text-accent-600 font-light text-xs">
          You do not have permission to view this page.
        </p>
        <p className="mt-2 text-accent-500 font-light text-xs">
          If you believe this is an error, please contact support.
        </p>
        <div className="pt-4">
          <CustomButton
            type="button"
            isFilled={true}
            horizontalPaddingClasses="px-4.5"
            verticalPaddingClasses="py-2"
            onClick={handleLogoutClick}
            dataQaId={'logout-button-unauthorized-screen'}
          >
            {NAMESPACES.LOGOUT_BUTTON}
          </CustomButton>
        </div>
      </div>
    </div>
  )
}

export default UserNotFoundPage
